import { Line3, Matrix3, Matrix4, Plane, Vector3 } from "three";
import { triangulate } from "./seidel/internal";
export var IntersectionMode;
(function (IntersectionMode) {
    IntersectionMode[IntersectionMode["DISCARD"] = -1] = "DISCARD";
    IntersectionMode[IntersectionMode["INTERSECT"] = 0] = "INTERSECT";
    IntersectionMode[IntersectionMode["INFRONT"] = 1] = "INFRONT";
    IntersectionMode[IntersectionMode["ALLVISIBLE"] = 2] = "ALLVISIBLE";
})(IntersectionMode || (IntersectionMode = {}));
export class Plane3 {
    constructor() {
        this.D = 0;
        this.nx = 0;
        this.ny = 0;
        this.nz = 0;
    }
    normalize() {
        const mag = Math.sqrt(this.nx * this.nx + this.ny * this.ny + this.nz * this.nz);
        this.nx /= mag;
        this.ny /= mag;
        this.nz /= mag;
        this.D /= mag;
    }
}
/**
 * Provides geometry calculation utility functions
 * @class GeometryUtils
 * @static
 * @author Robert Wüest, Benjamin Loesch
 * @constructor
 */
export class GeometryUtils {
    /**
     * Calculates the distance between two points.
     * @method CalcDistance
     *
     * @param  p1
     * @param  p2
     * @return {number}
     */
    static CalcDistance(p1, p2) {
        return Math.sqrt((p1.Easting - p2.Easting) ** 2 + (p1.Northing - p2.Northing) ** 2 + (p1.Height - p2.Height) ** 2);
    }
    static CalcDistance2D(p1, p2) {
        return Math.sqrt((p1.Easting - p2.Easting) ** 2 + (p1.Northing - p2.Northing) ** 2);
    }
    static CalcDistanceLine(points) {
        let res = 0.0;
        let p1 = points[0];
        for (let i = 1; i < points.length; i++) {
            const p2 = points[i];
            res = res + GeometryUtils.CalcDistance(p1, p2);
            p1 = p2;
        }
        return res;
    }
    static CalcDistanceLine2D(points) {
        let res = 0.0;
        let p1 = points[0];
        for (let i = 1; i < points.length; i++) {
            const p2 = points[i];
            res = res + GeometryUtils.CalcDistance2D(p1, p2);
            p1 = p2;
        }
        return res;
    }
    static CalcHeightLine(points) {
        // FIXME: do this everywhere, length check and defaults non 0.0.
        if (points.length < 2)
            return 0.0;
        const first = points[0];
        const last = points[points.length - 1];
        return last.Height - first.Height;
    }
    /**
     * Create line geometry (as in polygons
     * @method BuildLineGeometries
     *
     * @param {number[][]} lines
     * @param {VertexSemantic} vertexSemantic
     * @return {number[][]}
     */
    static BuildLineGeometries(lines, vertexSemantic) {
        const linegeometry = [
            [1.0, -1.0, 0.0], // COORD Start   0
            [1.0, 1.0, 0.0], // COORD End     1
            [0.0, 1.0, 0.0], // COORD End     2
            [0.0, -1.0, 0.0], // COORD Start  3
            [1.0, -1.0, 0.0], // COORD Start  0
            [0.0, 1.0, 0.0], // COORD End    2
        ];
        const coordMap = [0, 1, 1, 0, 0, 1];
        const indexMap = [0, 1, 2, 3, 0, 2];
        const uvMap = [
            [1.0, 1.0],
            [1.0, 0.0],
            [0.0, 0.0],
            [0.0, 1.0],
        ];
        const vertices = [];
        for (let h = 0; h < lines.length - 1; h++) {
            const start = [
                lines[h][vertexSemantic.vertices[0]],
                lines[h][vertexSemantic.vertices[1]],
                lines[h][vertexSemantic.vertices[2]],
            ];
            const end = [
                lines[h + 1][vertexSemantic.vertices[0]],
                lines[h + 1][vertexSemantic.vertices[1]],
                lines[h + 1][vertexSemantic.vertices[2]],
            ];
            let startColor = [];
            let endColor = [];
            if (vertexSemantic.hasColor) {
                startColor = [
                    lines[h][vertexSemantic.colors[0]],
                    lines[h][vertexSemantic.colors[1]],
                    lines[h][vertexSemantic.colors[2]],
                ];
                endColor = [
                    lines[h + 1][vertexSemantic.colors[0]],
                    lines[h + 1][vertexSemantic.colors[1]],
                    lines[h + 1][vertexSemantic.colors[2]],
                ];
            }
            for (let i = 0; i < linegeometry.length; i++) {
                const vertex = [];
                // line quad vertex
                vertex[vertexSemantic.vertices[0]] = linegeometry[i][0];
                vertex[vertexSemantic.vertices[1]] = linegeometry[i][1];
                vertex[vertexSemantic.vertices[2]] = linegeometry[i][2];
                if (vertexSemantic.hasColor) {
                    // color
                    const color = i == 0 || i == 1 || i == 4 ? endColor : startColor;
                    vertex[vertexSemantic.colors[0]] = color[0];
                    vertex[vertexSemantic.colors[1]] = color[1];
                    vertex[vertexSemantic.colors[2]] = color[2];
                }
                vertex.push(uvMap[indexMap[i]][0]);
                vertex.push(uvMap[indexMap[i]][1]);
                if (vertexSemantic.hasIndex) {
                    vertex.push(lines[h][vertexSemantic.index]);
                }
                else {
                    vertex.push(indexMap[i] + h * 6);
                }
                // start point
                vertex.push(start[0]);
                vertex.push(start[1]);
                vertex.push(start[2]);
                // end point
                vertex.push(end[0]);
                vertex.push(end[1]);
                vertex.push(end[2]);
                // Flag: start(0) or end(1)
                vertex.push(coordMap[i]);
                vertices.push(vertex);
            }
        }
        return vertices;
    }
    static Triangulate(points3D, vertexSemantic) {
        const pl = GeometryUtils.FitPlaneRaw(points3D);
        const plane = pl.plane;
        const orthoTransform = pl.transform;
        const points2D = [[]];
        const vS = vertexSemantic;
        // triangulate
        for (let i = 0; i < points3D.length; i++) {
            const target = new Vector3();
            const projPoint = plane.projectPoint(new Vector3(points3D[i][vS.vertices[0]], points3D[i][vS.vertices[1]], points3D[i][vS.vertices[2]]), target);
            projPoint.applyMatrix4(orthoTransform);
            const p2D = [projPoint.x, projPoint.y];
            if (vS.index >= 0)
                p2D[2] = points3D[i][vS.index];
            points2D[0].push(p2D);
        }
        const triangles = triangulate(points2D);
        if (triangles == null)
            return [];
        // create triangulated vertex list
        const vertices = [];
        for (let j = 0; j < triangles.length; j++) {
            for (let jj = 0; jj < triangles[j].length; jj++) {
                if (vS.index >= 0) {
                    for (let k = 0; k < points3D.length; k++) {
                        const index = points3D[k][vS.index];
                        if (index == triangles[j][jj].index) {
                            vertices.push(points3D[k]);
                            break;
                        }
                    }
                }
                else {
                    vertices.push(points3D[triangles[j][jj].index]);
                }
            }
        }
        return vertices;
    }
    static FitPlaneRaw(points) {
        const vectors = [];
        for (let i = 0; i < points.length; i++) {
            vectors.push(new Vector3(points[i][0], points[i][1], points[i][2]));
        }
        return GeometryUtils.FitPlane(vectors);
    }
    /**
     * @method FitPlane
     * @static
     * @param {Vector3[]} points
     * @return {Plane}
     */
    static FitPlane(points) {
        /* Subtract the mean to prevent numerical problems...*/
        const nbrPoints = points.length;
        if (nbrPoints < 3)
            return null;
        let mx = 0;
        let my = 0;
        let mz = 0;
        const pointsAdjusted = [];
        for (let i = 0; i < nbrPoints; i++) {
            mx = mx + (1 / nbrPoints) * points[i].x;
            my = my + (1 / nbrPoints) * points[i].y;
            mz = mz + (1 / nbrPoints) * points[i].z;
        }
        for (let i = 0; i < nbrPoints; i++) {
            const x = points[i].x - mx;
            const y = points[i].y - my;
            const z = points[i].z - mz;
            pointsAdjusted[i] = new Vector3(x, y, z);
        }
        // 2. calculate the best fit surface
        //2.a Get the covariance matrix
        const matcov = GeometryUtils.GetCovarianceMatrix(pointsAdjusted);
        const el = matcov.toArray();
        const M = [
            [el[0], el[1], el[2]],
            [el[3], el[4], el[5]],
            [el[6], el[7], el[8]],
        ];
        //2.b calc the singular values
        const svd = GeometryUtils.Svd(M);
        const sf = new Vector3(svd.S[0], svd.S[1], svd.S[2]);
        sf.normalize();
        const pfactor = Math.max(sf.x, Math.max(sf.y, sf.z));
        //2.c create the transform matrix -> smallest singular value-vector is set to 0
        const matTransform = new Matrix4();
        matTransform.set(svd.V[0][0], svd.V[0][1], svd.V[0][2], 0, svd.V[1][0], svd.V[1][1], svd.V[1][2], 0, svd.V[2][0], svd.V[2][1], svd.V[2][2], 0, 0, 0, 0, 1);
        const r1 = new Vector3(mx, my, mz); //plane vector (ortsvector)
        const n = new Vector3(svd.V[0][2], svd.V[1][2], svd.V[2][2]).normalize(); //normal vector
        const p = new Plane().setFromNormalAndCoplanarPoint(n, r1);
        return { plane: p, transform: matTransform, pfactor: pfactor };
    }
    /**
     * calculates the top-view area defined by a set of points. The points are projected to the horizontal plane
     *
     * @param {Vector3[]} points an array of vec3 points.
     * @return {any}
     */
    static CalcArea2D(points) {
        if (!(points.length > 2))
            return 0.0;
        let ps;
        if (points[0].Easting) {
            ps = points.map((e) => {
                return new Vector3(e.Easting, e.Northing, e.Height);
            });
        }
        else {
            ps = points;
        }
        // NOTE: Gauss'sche Flaechenformel
        let res = 0.0;
        for (let i = 0; i < ps.length; i++) {
            let iM = (i - 1) % ps.length;
            if (i - 1 < 0)
                iM = (i - 1 + ps.length) % ps.length;
            const iP = (i + 1) % ps.length;
            res += ps[i].y * (ps[iM].x - ps[iP].x);
        }
        res = res * 0.5;
        return Math.abs(res);
    }
    /**
     * calculates the area defined by a set of points. The points are projected to a mean plane.
     *
     * @param {Vector3[]} points an array of vec3 points.
     * @return {number}
     */
    static CalcArea(points) {
        if (!(points.length > 2))
            return 0.0;
        let ps;
        if (points[0].Easting) {
            ps = points.map((e) => {
                return new Vector3(e.Easting, e.Northing, e.Height);
            });
        }
        else {
            ps = points;
        }
        const plane = GeometryUtils.FitPlane(ps);
        if (plane != null) {
            for (const e of ps) {
                e.applyMatrix4(plane.transform);
            }
            return GeometryUtils.CalcArea2D(ps);
        }
        return 0.0;
    }
    static GetCovarianceMatrix(points) {
        const covmat = new Matrix3();
        //determine mean values
        const nmbrPoints = points.length;
        let mx = 0;
        let my = 0;
        let mz = 0;
        for (let i = 0; i < nmbrPoints; i++) {
            mx = mx + points[i].x / nmbrPoints;
            my = my + points[i].y / nmbrPoints;
            mz = mz + points[i].z / nmbrPoints;
        }
        const invden = 1 / (nmbrPoints - 1);
        let xx = 0;
        let xy = 0;
        let xz = 0;
        let yz = 0;
        let yy = 0;
        let zz = 0;
        for (let i = 0; i < nmbrPoints; i++) {
            //var(xx)
            xx += invden * (points[i].x - mx) * (points[i].x - mx);
            //var(xy)
            xy += invden * (points[i].x - mx) * (points[i].y - my);
            //var(xz)
            xz += invden * (points[i].x - mx) * (points[i].z - mz);
            //var(yz)
            yz += invden * (points[i].y - my) * (points[i].z - mz);
            //var(y,y)
            yy += invden * (points[i].y - my) * (points[i].y - my);
            //var(z,z)
            zz += invden * (points[i].z - mz) * (points[i].z - mz);
        }
        covmat.set(xx, xy, xz, xy, yy, yz, xz, yz, zz);
        return covmat;
    }
    /*
    Out of numericjs library - http://www.numericjs.com/
    Shanti Rao sent me this routine by private email. I had to modify it
    slightly to work on Arrays instead of using a Matrix object.
    It is apparently translated from http://stitchpanorama.sourceforge.net/Python/svd.py
    */
    static Svd(A) {
        let temp;
        //Compute the thin SVD from G. H. Golub and C. Reinsch, Numer. Math. 14, 403-420 (1970)
        let prec = Number.EPSILON;
        const tolerance = 1e-12;
        const itmax = 50;
        let c = 0;
        let i = 0;
        let j = 0;
        let k = 0;
        let l = 0;
        const u = A;
        const m = u.length;
        const n = u[0].length;
        if (m < n)
            throw "Need more rows than columns";
        const e = new Array(n);
        const q = new Array(n);
        for (i = 0; i < n; i++)
            e[i] = q[i] = 0.0;
        const v = GeometryUtils.Rep([n, n], 0, 0);
        function pythag(a, b) {
            a = Math.abs(a);
            b = Math.abs(b);
            if (a > b) {
                return a * Math.sqrt(1.0 + (b * b) / a / a);
            }
            else if (b == 0.0) {
                return a;
            }
            return b * Math.sqrt(1.0 + (a * a) / b / b);
        }
        //Householder's reduction to bidiagonal form
        let f = 0.0;
        let g = 0.0;
        let h = 0.0;
        let x = 0.0;
        let y = 0.0;
        let z = 0.0;
        let s = 0.0;
        for (i = 0; i < n; i++) {
            e[i] = g;
            s = 0.0;
            l = i + 1;
            for (j = i; j < m; j++)
                s += u[j][i] * u[j][i];
            if (s <= tolerance)
                g = 0.0;
            else {
                f = u[i][i];
                g = Math.sqrt(s);
                if (f >= 0.0)
                    g = -g;
                h = f * g - s;
                u[i][i] = f - g;
                for (j = l; j < n; j++) {
                    s = 0.0;
                    for (k = i; k < m; k++)
                        s += u[k][i] * u[k][j];
                    f = s / h;
                    for (k = i; k < m; k++)
                        u[k][j] += f * u[k][i];
                }
            }
            q[i] = g;
            s = 0.0;
            for (j = l; j < n; j++)
                s = s + u[i][j] * u[i][j];
            if (s <= tolerance)
                g = 0.0;
            else {
                f = u[i][i + 1];
                g = Math.sqrt(s);
                if (f >= 0.0)
                    g = -g;
                h = f * g - s;
                u[i][i + 1] = f - g;
                for (j = l; j < n; j++)
                    e[j] = u[i][j] / h;
                for (j = l; j < m; j++) {
                    s = 0.0;
                    for (k = l; k < n; k++)
                        s += u[j][k] * u[i][k];
                    for (k = l; k < n; k++)
                        u[j][k] += s * e[k];
                }
            }
            y = Math.abs(q[i]) + Math.abs(e[i]);
            if (y > x)
                x = y;
        }
        // accumulation of right hand gtransformations
        for (i = n - 1; i != -1; i += -1) {
            if (g != 0.0) {
                h = g * u[i][i + 1];
                for (j = l; j < n; j++)
                    v[j][i] = u[i][j] / h;
                for (j = l; j < n; j++) {
                    s = 0.0;
                    for (k = l; k < n; k++)
                        s += u[i][k] * v[k][j];
                    for (k = l; k < n; k++)
                        v[k][j] += s * v[k][i];
                }
            }
            for (j = l; j < n; j++) {
                v[i][j] = 0;
                v[j][i] = 0;
            }
            v[i][i] = 1;
            g = e[i];
            l = i;
        }
        // accumulation of left hand transformations
        for (i = n - 1; i != -1; i += -1) {
            l = i + 1;
            g = q[i];
            for (j = l; j < n; j++)
                u[i][j] = 0;
            if (g != 0.0) {
                h = u[i][i] * g;
                for (j = l; j < n; j++) {
                    s = 0.0;
                    for (k = l; k < m; k++)
                        s += u[k][i] * u[k][j];
                    f = s / h;
                    for (k = i; k < m; k++)
                        u[k][j] += f * u[k][i];
                }
                for (j = i; j < m; j++)
                    u[j][i] = u[j][i] / g;
            }
            else
                for (j = i; j < m; j++)
                    u[j][i] = 0;
            u[i][i] += 1;
        }
        // diagonalization of the bidiagonal form
        prec = prec * x;
        for (k = n - 1; k != -1; k += -1) {
            for (let iteration = 0; iteration < itmax; iteration++) {
                // test f splitting
                let test_convergence = false;
                for (l = k; l != -1; l += -1) {
                    if (Math.abs(e[l]) <= prec) {
                        test_convergence = true;
                        break;
                    }
                    if (Math.abs(q[l - 1]) <= prec)
                        break;
                }
                if (!test_convergence) {
                    // cancellation of e[l] if l>0
                    c = 0.0;
                    s = 1.0;
                    const l1 = l - 1;
                    for (i = l; i < k + 1; i++) {
                        f = s * e[i];
                        e[i] = c * e[i];
                        if (Math.abs(f) <= prec)
                            break;
                        g = q[i];
                        h = pythag(f, g);
                        q[i] = h;
                        c = g / h;
                        s = -f / h;
                        for (j = 0; j < m; j++) {
                            y = u[j][l1];
                            z = u[j][i];
                            u[j][l1] = y * c + z * s;
                            u[j][i] = -y * s + z * c;
                        }
                    }
                }
                // test f convergence
                z = q[k];
                if (l == k) {
                    //convergence
                    if (z < 0.0) {
                        //q[k] is made non-negative
                        q[k] = -z;
                        for (j = 0; j < n; j++)
                            v[j][k] = -v[j][k];
                    }
                    break; //break out of iteration loop and move on to next k value
                }
                if (iteration >= itmax - 1)
                    throw "Error: no convergence.";
                // shift from bottom 2x2 minor
                x = q[l];
                y = q[k - 1];
                g = e[k - 1];
                h = e[k];
                f = ((y - z) * (y + z) + (g - h) * (g + h)) / (2.0 * h * y);
                g = pythag(f, 1.0);
                if (f < 0.0)
                    f = ((x - z) * (x + z) + h * (y / (f - g) - h)) / x;
                else
                    f = ((x - z) * (x + z) + h * (y / (f + g) - h)) / x;
                // next QR transformation
                c = 1.0;
                s = 1.0;
                for (i = l + 1; i < k + 1; i++) {
                    g = e[i];
                    y = q[i];
                    h = s * g;
                    g = c * g;
                    z = pythag(f, h);
                    e[i - 1] = z;
                    c = f / z;
                    s = h / z;
                    f = x * c + g * s;
                    g = -x * s + g * c;
                    h = y * s;
                    y = y * c;
                    for (j = 0; j < n; j++) {
                        x = v[j][i - 1];
                        z = v[j][i];
                        v[j][i - 1] = x * c + z * s;
                        v[j][i] = -x * s + z * c;
                    }
                    z = pythag(f, h);
                    q[i - 1] = z;
                    c = f / z;
                    s = h / z;
                    f = c * g + s * y;
                    x = -s * g + c * y;
                    for (j = 0; j < m; j++) {
                        y = u[j][i - 1];
                        z = u[j][i];
                        u[j][i - 1] = y * c + z * s;
                        u[j][i] = -y * s + z * c;
                    }
                }
                e[l] = 0.0;
                e[k] = f;
                q[k] = x;
            }
        }
        for (i = 0; i < q.length; i++)
            if (q[i] < prec)
                q[i] = 0;
        //sort eigenvalues
        for (i = 0; i < n; i++) {
            //writeln(q)
            for (j = i - 1; j >= 0; j--) {
                if (q[j] < q[i]) {
                    c = q[j];
                    q[j] = q[i];
                    q[i] = c;
                    for (k = 0; k < u.length; k++) {
                        temp = u[k][i];
                        u[k][i] = u[k][j];
                        u[k][j] = temp;
                    }
                    for (k = 0; k < v.length; k++) {
                        temp = v[k][i];
                        v[k][i] = v[k][j];
                        v[k][j] = temp;
                    }
                    i = j;
                }
            }
        }
        return { U: u, S: q, V: v };
    }
    static Rep(s, v, k) {
        if (typeof k === "undefined")
            k = 0;
        const n = s[k];
        const ret = Array(n);
        let i;
        if (k === s.length - 1) {
            for (i = n - 2; i >= 0; i -= 2) {
                ret[i + 1] = v;
                ret[i] = v;
            }
            if (i === -1)
                ret[0] = v;
            return ret;
        }
        for (i = n - 1; i >= 0; i--) {
            ret[i] = GeometryUtils.Rep(s, v, k + 1);
        }
        return ret;
    }
    /**
     * Check if point is inside polygon
     * @method PointInsidePolygon2D
     * @param {Vector2[]} poly
     * @param {Vector2} pt
     * @return boolean
     * @static
     */
    static PointInsidePolygon2D(poly, pt) {
        let c;
        let i;
        let l;
        let j;
        for (c = false, i = -1, l = poly.length, j = l - 1; ++i < l; j = i)
            ((poly[i].y <= pt.y && pt.y < poly[j].y) || (poly[j].y <= pt.y && pt.y < poly[i].y)) &&
                pt.x < ((poly[j].x - poly[i].x) * (pt.y - poly[i].y)) / (poly[j].y - poly[i].y) + poly[i].x &&
                (c = !c);
        return c;
    }
    /**
     * determine side of point (px,py,pz) (half space)
     * @param {Plane3} plane
     * @param {number} px
     * @param {number} py
     * @param {number} pz
     * @return {number}
     */
    static SideOfPlane(plane, px, py, pz) {
        return plane.nx * px + plane.ny * py + plane.nz * pz + plane.D;
    }
    static ClipGeometryWithPlane(vertices, plane) {
        const clippedVertices = [];
        for (let i = 0; i < vertices.length; i++) {
            const inc = i < vertices.length - 1 ? i + 1 : 0;
            const a = vertices[i];
            const b = vertices[inc];
            const intersectAB = GeometryUtils.Intersect3dLine(a[0], a[1], a[2], b[0], b[1], b[2], plane);
            if (intersectAB["state"] != IntersectionMode.DISCARD) {
                clippedVertices.push([
                    intersectAB["intersection"][0],
                    intersectAB["intersection"][1],
                    intersectAB["intersection"][2],
                ]);
                if (vertices[i].length > 3) {
                    clippedVertices[clippedVertices.length - 1].push(vertices[i][3]);
                }
                if (intersectAB["state"] == IntersectionMode.INFRONT) {
                    const intersectBA = GeometryUtils.Intersect3dLine(b[0], b[1], b[2], a[0], a[1], a[2], plane);
                    clippedVertices.push([
                        intersectBA["intersection"][0],
                        intersectBA["intersection"][1],
                        intersectBA["intersection"][2],
                    ]);
                    if (vertices[i].length > 3) {
                        clippedVertices[clippedVertices.length - 1].push(vertices[i][3]);
                    }
                }
            }
        }
        return clippedVertices;
    }
    /**
     * Clips a 3d Line defined by two points in internal coordinates  (engine.CoordToIntern(..))
     *
     * @param {number} p1_x
     * @param {number} p1_y
     * @param {number} p1_z
     * @param {number} p2_x
     * @param {number} p2_y
     * @param {number} p2_z
     * @param plane
     * @returns Array.<number> the clipped internal coordinates
     */
    static Intersect3dLine(p1_x, p1_y, p1_z, p2_x, p2_y, p2_z, plane) {
        const distancep1 = GeometryUtils.SideOfPlane(plane, p1_x, p1_y, p1_z);
        const distancep2 = GeometryUtils.SideOfPlane(plane, p2_x, p2_y, p2_z);
        const t = Math.sign(distancep1) + Math.sign(distancep2);
        if (t < 0) {
            // discard is behind the plane
            return { state: IntersectionMode.DISCARD, intersection: [0, 0, 0] };
        }
        else if (distancep1 >= 0 /*  || t > 0*/) {
            // is in front of the plane
            if (distancep2 >= 0) {
                return { state: IntersectionMode.ALLVISIBLE, intersection: [p1_x, p1_y, p1_z] };
            }
            else {
                return { state: IntersectionMode.INFRONT, intersection: [p1_x, p1_y, p1_z] };
            }
        }
        // intersects
        const s = distancep1 / (distancep1 - distancep2);
        const pi_x = p1_x + s * (p2_x - p1_x);
        const pi_y = p1_y + s * (p2_y - p1_y);
        const pi_z = p1_z + s * (p2_z - p1_z);
        return { state: IntersectionMode.INTERSECT, intersection: [pi_x, pi_y, pi_z] };
    }
    /**
     * Clips a 3d Line defined by two points in internal coordinates  (engine.CoordToIntern(..))
     * @method Clip3dLine
     * @param {Frustum} frustum
     * @param {Vector3} point1
     * @param {Vector3} point2
     * @returns Array.<number> the clipped internal coordinates
     * @static
     */
    static Clip3dLine(frustum, point1, point2) {
        //determine wich point is inside frustum
        const p1isInside = this.frustumContainsPoint(frustum, point1);
        const p2isInside = this.frustumContainsPoint(frustum, point2);
        if (p1isInside && p2isInside)
            return [point1, point2]; //both points are inside
        //create a line and calc intersection with all frustum planes
        // store intersection points in ips[];
        const line = new Line3(point1, point2);
        const ips = []; //intersectionpoints
        for (let i = 0; i < frustum.planes.length; i++) {
            const target = new Vector3();
            const ip = frustum.planes[i].intersectLine(line, target);
            if (ip) {
                ips.push(ip);
            }
        }
        if (ips.length == 0)
            return null; //line lies completely inside frustum
        if (ips.length > 0) {
            //there is at least one intersection
            // check if the plane-line intersection lies inside frustum and store all in ipsinside
            const ipsinside = [];
            for (let i = 0; i < ips.length; i++) {
                if (this.frustumContainsPoint(frustum, ips[i]))
                    ipsinside.push(ips[i]);
            }
            if (ipsinside.length > 2) {
                //this is impossible!
                console.log("impossible!!!");
            }
            if (ipsinside.length == 2) {
                //start and end are lying outside frustum but line intersects frustum!
                return [ipsinside[0], ipsinside[1]];
            }
            if (ipsinside.length == 1) {
                //there is an intersection with one frustum plane
                if (p1isInside)
                    return [point1, ipsinside[0]];
                if (p2isInside)
                    return [ipsinside[0], point2];
            }
            if (ipsinside.length == 0) {
                //more than two intersection points but no point is inside frustum -> impossible!!!
                for (let j = 0; j < ips.length; j++) {
                    const intersects = this.frustumContainsPoint(frustum, ips[j]);
                    if (intersects) {
                        if (p1isInside)
                            return [point1, ips[j]];
                        if (p2isInside)
                            return [ips[j], point2];
                    }
                }
            }
        }
    }
    static frustumContainsPoint(frustum, p) {
        for (let i = 0; i < 6; i++) {
            if (frustum.planes[i].distanceToPoint(p) < -0.000001)
                return false; // ips[j] is outside
        }
        return true;
    }
    /**
     * Checks if point is in front of nearplane -> this means inside frustum
     * @param frustum
     * @param p
     * @returns {boolean}
     */
    static pointInFrontOfNearPlane(frustum, p) {
        //0.1 to prevent numerical problems...
        // ips[j] is outside
        return frustum.planes[5].distanceToPoint(p) >= 0;
    }
    /**
     * Checks if point is in front of farplane -> this means inside frustum
     * @param frustum
     * @param p
     * @returns {boolean}
     */
    static pointInFrontOfFarPlane(frustum, p) {
        //0.1 to prevent numerical problems...
        // ips[j] is outside
        return frustum.planes[4].distanceToPoint(p) >= 0;
    }
    /**
     * @method Clip3dLine
     * @param {Frustum} frustum
     * @param {Vector3} point1
     * @param {Vector3} point2
     * @returns Array.<number> the clipped internal coordinates
     */
    static ClipNearPlane(frustum, point1, point2) {
        //determine wich point is in front of frustum
        const p1isInFrontOfPlane = this.pointInFrontOfNearPlane(frustum, point1);
        const p2isInFrontOfPlane = this.pointInFrontOfNearPlane(frustum, point2);
        if (p1isInFrontOfPlane)
            return null; //both points are inside
        if (!p1isInFrontOfPlane && !p2isInFrontOfPlane)
            return new Vector3();
        //create a line and calc intersection with all frustum planes
        // store intersection points in ips[];
        const line = new Line3(point1, point2);
        frustum.planes[5].constant -= 0.1;
        const target = new Vector3();
        const ip = frustum.planes[5].intersectLine(line, target);
        if (ip)
            return ip;
        return null;
    }
    /**
     * @method Clip3dLine
     * @param {Frustum} frustum
     * @param {Vector3} point1
     * @param {Vector3} point2
     * @returns Array.<number> the clipped internal coordinates
     * @static
     */
    static ClipNearFarPlane(frustum, point1, point2) {
        frustum.planes[4].constant -= 0.001;
        frustum.planes[5].constant -= 0.001; // change the nearplane position a bit
        //determine wich point is in front of frustum
        const p1isBetween = this.pointInFrontOfNearPlane(frustum, point1) && this.pointInFrontOfFarPlane(frustum, point1);
        const p2isBetween = this.pointInFrontOfNearPlane(frustum, point2) && this.pointInFrontOfFarPlane(frustum, point2);
        if (p1isBetween && p2isBetween)
            return [point1, point2]; //both points are inside
        //create a line and calc intersection with near and far plane
        // store intersection points in ips[];
        const line = new Line3(point1, point2);
        const target = new Vector3();
        const ip1 = frustum.planes[5].intersectLine(line, target);
        if (ip1) {
            if (p1isBetween) {
                return [point1, ip1];
            }
            else if (p2isBetween) {
                return [ip1, point2];
            }
        }
        //check far plane
        const target2 = new Vector3();
        const ip2 = frustum.planes[4].intersectLine(line, target2);
        if (ip2) {
            if (p1isBetween) {
                return [point1, ip2];
            }
            else if (p2isBetween) {
                return [ip2, point2];
            }
        }
        //both point are outside, but line intersects frustum
        if (ip1 && ip2) {
            //check wich point is first
            if (point1.distanceTo(ip1) > point1.distanceTo(ip2)) {
                return [ip2, ip1];
            }
            else {
                return [ip1, ip2];
            }
        }
        return null;
    }
    static internToMouse(interncoord, mvp, engine) {
        //check if frustum contains point?
        const pickpos_screen = new Vector3();
        pickpos_screen.copy(interncoord).applyMatrix4(mvp);
        let isInImage = true;
        if (pickpos_screen.x < -1 ||
            pickpos_screen.x > 1 ||
            isNaN(pickpos_screen.x) ||
            pickpos_screen.y < -1 ||
            pickpos_screen.y > 1 ||
            isNaN(pickpos_screen.y) ||
            pickpos_screen.z < -1 ||
            pickpos_screen.z > 1 ||
            isNaN(pickpos_screen.z)) {
            isInImage = false;
        }
        const w = engine.getContextWidth();
        const h = engine.getContextHeight();
        const x = w * 0.5 * (pickpos_screen.x + 1);
        const y = h - h * 0.5 * (pickpos_screen.y + 1);
        return new Vector3(x, y, +isInImage);
    }
    static pick3dline(linestringcoords, mousex, mousey, measurehandler) {
        let minima = 10e10;
        let pointindex = -1;
        let mp = [0, 0];
        for (let i = 0; i < linestringcoords.length - 1; i++) {
            const pos0 = linestringcoords[i];
            const pos1 = linestringcoords[i + 1];
            const linepos = measurehandler.worldLineToMouseLine(pos0, pos1);
            if (linepos == null)
                continue; //line segment is not within view frustum
            // Segment A(xa, ya), B(xb, yb) and original mouse coord C(xc,yc).
            const cx = mousex;
            const cy = mousey;
            const ax = linepos[0];
            const ay = linepos[1];
            const bx = linepos[2];
            const by = linepos[3];
            const r_numerator = (cx - ax) * (bx - ax) + (cy - ay) * (by - ay);
            const r_denomenator = (bx - ax) * (bx - ax) + (by - ay) * (by - ay);
            const r = r_numerator / r_denomenator;
            const s = ((ay - cy) * (bx - ax) - (ax - cx) * (by - ay)) / r_denomenator;
            const distanceLine = Math.abs(s) * Math.sqrt(r_denomenator);
            if (r >= 0 && r <= 1) {
                if (distanceLine < minima) {
                    minima = distanceLine;
                    if (r < 0.5) {
                        pointindex = i;
                        mp = [ax, ay];
                    }
                    else {
                        pointindex = i + 1;
                        mp = [bx, by];
                    }
                }
            }
        }
        if (minima < 15)
            return { mp: mp, pointindex: pointindex, distance: minima };
        return null;
    }
    static clamp(value, a, b) {
        return Math.min(Math.max(value, a), b);
    }
}
//# sourceMappingURL=geometryutils.js.map